import React, { useState, useEffect } from 'react';
import { aiAPI } from '../services/aiBackend';

const AIInsights = ({ data, platform }) => {
  const [insights, setInsights] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const generateInsights = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Create a context-aware prompt for insights
      const prompt = `Analyze the following ${platform} data and provide actionable insights:

Data: ${JSON.stringify(data, null, 2)}

Please provide:
1. Key performance insights
2. Growth opportunities
3. Content recommendations
4. Engagement strategies

Format as a structured response with clear sections.`;

      const response = await aiAPI.generateContent({
        prompt: prompt,
        platform: platform,
        contentType: 'analysis',
        maxLength: 1000,
        includeHashtags: false,
        includeEmojis: true,
        userId: 'dashboard-user'
      });

      setInsights(response.generatedContent || response.content || response.message);
    } catch (error) {
      console.error('AI Insights Error:', error);
      setError('Failed to generate insights');
      
      // Provide fallback insights
      setInsights(generateFallbackInsights(data, platform));
    } finally {
      setIsLoading(false);
    }
  };

  const generateFallbackInsights = (data, platform) => {
    const followers = data?.followers || 0;
    const engagement = data?.engagement || 0;
    const trend = data?.trend || '+0%';

    return `📊 **${platform.toUpperCase()} PERFORMANCE INSIGHTS**

🎯 **Current Status:**
• ${followers.toLocaleString()} followers
• ${engagement}% engagement rate
• ${trend} growth trend

💡 **Growth Opportunities:**
• Increase posting frequency during peak hours
• Create more interactive content (polls, Q&As)
• Collaborate with other comedy shows
• Use trending hashtags in your niche

📈 **Content Recommendations:**
• Behind-the-scenes content (40% higher engagement)
• Comedian spotlights and interviews
• Audience testimonials and reactions
• Show highlights and best moments

🎭 **Engagement Strategies:**
• Respond to comments within 2 hours
• Create user-generated content campaigns
• Host live Q&A sessions
• Share exclusive backstage content

⚡ **Quick Actions:**
• Post 2-3 times per week consistently
• Use 5-10 relevant hashtags per post
• Engage with followers' content
• Monitor analytics weekly

*To unlock AI-powered insights, configure your API keys in the deployment.*`;
  };

  useEffect(() => {
    if (data && Object.keys(data).length > 0) {
      generateInsights();
    }
  }, [data, platform]);

  if (!data || Object.keys(data).length === 0) {
    return null;
  }

  return (
    <div className="ai-insights-card">
      <div className="ai-insights-header">
        <div className="ai-insights-title">
          <div className="ai-avatar">🤖</div>
          <h3>AI Insights</h3>
        </div>
        <button 
          className="ai-insights-refresh"
          onClick={generateInsights}
          disabled={isLoading}
        >
          {isLoading ? '⏳' : '🔄'}
        </button>
      </div>

      <div className="ai-insights-content">
        {isLoading ? (
          <div className="ai-insights-loading">
            <div className="ai-typing">
              <span></span>
              <span></span>
              <span></span>
            </div>
            <p>Generating insights...</p>
          </div>
        ) : error ? (
          <div className="ai-insights-error">
            <p>⚠️ {error}</p>
            <button onClick={generateInsights} className="retry-btn">
              Try Again
            </button>
          </div>
        ) : insights ? (
          <div className="ai-insights-text">
            {insights.split('\n').map((line, index) => {
              if (line.trim() === '') return <br key={index} />;
              
              if (line.startsWith('📊') || line.startsWith('🎯') || line.startsWith('💡') || 
                  line.startsWith('📈') || line.startsWith('🎭') || line.startsWith('⚡')) {
                return (
                  <h4 key={index} className="insight-section">
                    {line}
                  </h4>
                );
              }
              
              if (line.startsWith('•')) {
                return (
                  <p key={index} className="insight-bullet">
                    {line}
                  </p>
                );
              }
              
              if (line.includes('**') && line.includes('**')) {
                const boldText = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
                return (
                  <p key={index} className="insight-bold" dangerouslySetInnerHTML={{ __html: boldText }} />
                );
              }
              
              return (
                <p key={index} className="insight-text">
                  {line}
                </p>
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default AIInsights;
